// UI metadata for the video compressor, ported from the sibling `billing` app.
//
// services/videoCompression.service.js is the SOURCE OF TRUTH for the actual ffmpeg params —
// everything here is labels and hints describing the choices. The preset KEYS are the contract
// between the two halves: `${quality}_${size}` must match the server's QUALITY × SIZE matrix,
// or the server 400s with "Unknown preset".

export const QUALITIES = [
  { key: 'best', label: 'Best Quality', hint: 'Sharpest (CRF 20)' },
  { key: 'medium', label: 'Medium Quality', hint: 'Balanced (CRF 26)' },
  { key: 'low', label: 'Low Quality', hint: 'Most compressed (CRF 32)' },
];

export const SIZES = [
  { key: 'small', label: 'Small File', hint: '≤ 480p · 96k audio' },
  { key: 'medium', label: 'Medium File', hint: '≤ 720p · 128k audio' },
  { key: 'large', label: 'Large File', hint: '≤ 1080p · 192k audio' },
];

export const presetKeyOf = (q, s) => `${q}_${s}`;

export const presetLabelOf = (q, s) => {
  const ql = QUALITIES.find((x) => x.key === q)?.label || q;
  const sl = SIZES.find((x) => x.key === s)?.label || s;
  return `${ql} + ${sl}`;
};

export const DEFAULT_PRESET = 'best_medium';

export const ADVANCED_DEFAULTS = {
  codec: 'libx264',
  crf: 26,
  resolution: 'original',
  fps: 'original',
  audioBitrate: '128k',
  removeAudio: false,
  speed: 'medium',
  format: 'mp4',
};

export const ADVANCED_OPTIONS = {
  codec: [
    { value: 'libx264', label: 'H.264 (MP4 · best compatibility)' },
    { value: 'libx265', label: 'H.265 / HEVC (MP4 · smaller files)' },
    { value: 'libvpx-vp9', label: 'VP9 (WebM)' },
  ],
  resolution: [
    { value: 'original', label: 'Original' },
    { value: '1080', label: '1080p' },
    { value: '720', label: '720p' },
    { value: '480', label: '480p' },
    { value: '360', label: '360p' },
  ],
  fps: [
    { value: 'original', label: 'Original' },
    { value: '30', label: '30 fps' },
    { value: '24', label: '24 fps' },
  ],
  audioBitrate: ['64k', '96k', '128k', '192k', '256k'],
  speed: ['ultrafast', 'superfast', 'veryfast', 'faster', 'fast', 'medium', 'slow', 'slower', 'veryslow'],
  format: [
    { value: 'mp4', label: 'MP4' },
    { value: 'webm', label: 'WebM' },
  ],
};

export function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}
